/**
 * Cart Totals Calculator — VAT-exclusive markets (AE, SA, QA, …)
 *
 * A single file that handles any VAT-exclusive Shopify market.
 * Country-specific values (code, currency, locale) come from
 * window.CartItemsDataVatExcl which is populated by
 * cart-totals-summary-vat-excl-js-version.liquid.
 *
 * All Shopify prices on VAT-exclusive markets are already excl-VAT.
 * VAT is added ON TOP: vatAmount = totalExclVat × vatRate.
 *
 * Adding a new VAT-exclusive country requires zero changes to this file.
 */

document.addEventListener('DOMContentLoaded', () => {
  const _log =
    typeof window.createLogger === 'function'
      ? window.createLogger('CartTotalsCalculatorVatExcl')
      : { log: () => {}, warn: () => {}, error: () => {} };

  _log.log('DOMContentLoaded fired');

  if (!window.CartItemsDataVatExcl) {
    _log.warn('CartItemsDataVatExcl not found');
    return;
  }

  let cartData = window.CartItemsDataVatExcl;
  const shippingCountries = cartData.shippingCountries;
  const dutyData = cartData.dutyData;

  // Derived from the active market — no hardcoding per country.
  const resolvedLocale = cartData.locale;
  const currency = cartData.currency;
  const suffix = cartData.countryCode.toLowerCase();
  const countryEventPrefix = cartData.countryCode.toUpperCase();

  _log.log('Country:', countryEventPrefix, '| Currency:', currency, '| Locale:', resolvedLocale, '| Suffix:', suffix);

  let currentCountryCode = cartData.countryCode;
  let currentShippingRateWithVAT = 0;

  // Prices are already excl-VAT on these markets — cartTotalWithVAT IS the excl-VAT amount.
  let cartTotalExclVatCents = cartData.cartTotalWithVAT;
  window.cartTotalVatExcl = cartTotalExclVatCents / 100;

  _log.log('Cart Total (excl. VAT, cents):', cartTotalExclVatCents);

  // =====================================================
  // Helper: build a suffixed element ID
  // =====================================================
  function el(baseId) {
    return `${baseId}-${suffix}`;
  }

  // =====================================================
  // Cart data update (from forklift / cart API)
  // =====================================================
  function updateCartData(shopifyCart) {
    // Prices from VAT-exclusive markets are already excl-VAT — no division needed.
    cartTotalExclVatCents = shopifyCart.total_price || 0;

    if (shopifyCart.items && Array.isArray(shopifyCart.items)) {
      cartData.items = shopifyCart.items.map((item) => {
        const orig = cartData.items.find((o) => o.id === item.id) || {};
        return {
          id: item.id,
          title: item.title,
          productTitle: item.product_title,
          variantTitle: item.variant_title,
          quantity: item.quantity,
          finalLinePrice: item.final_line_price,
          sku: item.sku,
          countryOfOrigin: orig.countryOfOrigin || null,
          hsCode: orig.hsCode || null,
          finalPrice: item.final_price
        };
      });
    }

    _log.log('Updated cart total (excl. VAT, cents):', cartTotalExclVatCents);
  }

  // =====================================================
  // VAT / duty helpers
  // =====================================================
  function getVatRate(countryCode) {
    if (typeof window.getVatRate === 'function') {
      const rate = window.getVatRate(countryCode);
      _log.log(`VAT rate for ${countryCode}:`, rate);
      return rate;
    }
    _log.warn('window.getVatRate not found, defaulting to 0');
    return 0;
  }

  function getAdminFeeWithoutVat(countryCode) {
    if (!shippingCountries || !shippingCountries[countryCode]) return 0;
    const countryData = shippingCountries[countryCode];

    // Chain: EUR → GBP (stored as admin_fee_gbp) → local currency via Shopify.currency.rate.
    // admin_fee_gbp is a one-time metafield entry; Shopify.currency.rate gives the live rate.
    const adminFeeGBP = parseFloat(countryData.admin_fee_gbp) || 0;
    if (!adminFeeGBP) {
      _log.warn('admin_fee_gbp missing for', countryCode, '— add it to the shippingCountries metafield.');
      return 0;
    }
    const adminFeeGBPCents = adminFeeGBP * 100;
    const gbpToLocal = parseFloat(window.Shopify?.currency?.rate) || 1;
    const adminFeeLocalCents = adminFeeGBPCents * gbpToLocal;
    _log.log('Admin fee — GBP cents:', adminFeeGBPCents, '| GBP→local rate:', gbpToLocal, '| local cents:', adminFeeLocalCents);
    return adminFeeLocalCents;
  }

  function getDutyRate(countryOfOrigin, destination, hsCode) {
    if (!dutyData || !countryOfOrigin || !destination || !hsCode) return 1.0;
    const raw = dutyData[countryOfOrigin]?.[destination]?.[hsCode];
    if (raw === undefined || raw === null) return 1.0;
    return raw === 1 ? 1.0 : raw / 100.0 + 1.0;
  }

  // =====================================================
  // Money formatting
  // =====================================================
  function formatMoneyAmount(amountCents) {
    if (typeof window.formatMoney === 'function') {
      return window.formatMoney(amountCents, { locale: resolvedLocale, currency, isCents: true });
    }
    return new Intl.NumberFormat(resolvedLocale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amountCents / 100);
  }

  // =====================================================
  // Core calculation
  // =====================================================
  function calculate() {
    try {
      _log.log('Calculating totals for country:', currentCountryCode);

      const vatRate = getVatRate(currentCountryCode);
      const adminFeeWithoutVatCents = getAdminFeeWithoutVat(currentCountryCode);
      // Shipping rates from VAT-exclusive markets are already excl-VAT.
      const shippingRateCentsWithoutVAT = currentShippingRateWithVAT;

      let totalItemsDutyCents = 0.0;
      let cartTotalDutyWeitage = 0.0;

      cartData.items.forEach((item) => {
        let origin = item.countryOfOrigin;
        let hsCode = item.hsCode;

        if (window.CartApiStockData?.products && item.sku) {
          const apiData = window.CartApiStockData.products[item.sku];
          if (apiData) {
            if (apiData.country_of_origin) origin = apiData.country_of_origin;
            if (apiData.hs_code) hsCode = apiData.hs_code;
          }
        }

        const dutyRate = getDutyRate(origin, currentCountryCode, hsCode);
        const itemPriceExclVatCents = item.finalLinePrice;
        const itemPriceWithDutyCents = itemPriceExclVatCents * dutyRate;

        totalItemsDutyCents += itemPriceWithDutyCents - itemPriceExclVatCents;
        cartTotalDutyWeitage += (itemPriceExclVatCents / cartTotalExclVatCents) * dutyRate;
      });

      const shippingWithDutyCents = shippingRateCentsWithoutVAT * cartTotalDutyWeitage;
      const shippingDutyCents = shippingWithDutyCents - shippingRateCentsWithoutVAT;
      const allTotalExclVatCents =
        cartTotalExclVatCents + totalItemsDutyCents + shippingWithDutyCents + adminFeeWithoutVatCents;

      // VAT is added ON TOP of the excl-VAT total (not extracted from it).
      const vatAmountCents = allTotalExclVatCents * vatRate;
      const totalDutyCents = totalItemsDutyCents + shippingDutyCents;
      const landedTotalCents = allTotalExclVatCents + vatAmountCents;
      const totalPayToUsCents = cartTotalExclVatCents + shippingRateCentsWithoutVAT;
      const totalPayToCourierCents = totalDutyCents + adminFeeWithoutVatCents + vatAmountCents;

      _log.log('Calculation complete:', {
        vatRate, cartTotalExclVatCents, totalItemsDutyCents,
        shippingDutyCents, allTotalExclVatCents, vatAmountCents, landedTotalCents
      });

      window.cartTotalVatExcl = cartTotalExclVatCents / 100;

      updateElement(el('cart-total-without-vat'),                   formatMoneyAmount(cartTotalExclVatCents));
      updateElement(el('duty-items-container'),                     '', true);
      updateElement(el('total-items-duty-amount-display'),          formatMoneyAmount(totalItemsDutyCents));
      toggleElementVisibility(el('total-items-duty-summary'),       totalItemsDutyCents > 0);
      updateElement(el('shipping-rate-display'),                    formatMoneyAmount(shippingRateCentsWithoutVAT));
      toggleElementVisibility(el('shipping-summary'),               shippingRateCentsWithoutVAT > 0);
      updateElement(el('shipping-duty-amount-display'),             formatMoneyAmount(shippingDutyCents));
      toggleElementVisibility(el('shipping-duty-summary'),          shippingDutyCents > 0);
      updateElement(el('admin-fee-amount-display'),                 formatMoneyAmount(adminFeeWithoutVatCents));
      updateElement(el('total-payable-without-vat-amount-display'), formatMoneyAmount(allTotalExclVatCents));
      updateElement(el('vat-rate-display'),                         parseFloat((vatRate * 100).toFixed(2)).toString());
      updateElement(el('vat-amount-display'),                       formatMoneyAmount(vatAmountCents));
      updateElement(el('landed-total-amount-display'),              formatMoneyAmount(landedTotalCents));
      updateElement(el('pay-to-us-amount-display'),                 formatMoneyAmount(totalPayToUsCents));
      updateElement(el('pay-to-courier-amount-display'),            formatMoneyAmount(totalPayToCourierCents));

      const dutyLabel = document.getElementById(el('duty-label'));
      if (dutyLabel) dutyLabel.style.display = totalItemsDutyCents > 0 ? 'inline' : 'none';

      showAllSections();
    } catch (error) {
      _log.error('Calculation error:', error);
    }
  }

  // =====================================================
  // DOM helpers
  // =====================================================
  function updateElement(id, value, isHTML = false) {
    const element = document.getElementById(id);
    if (!element) return;
    if (isHTML) element.innerHTML = value;
    else element.textContent = value;
  }

  function toggleElementVisibility(id, shouldShow) {
    const element = document.getElementById(id);
    if (!element) {
      _log.warn(`toggleElementVisibility: element "${id}" not found`);
      return;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      element.style.display = shouldShow ? '' : 'none';
      return;
    }

    if (!element.dataset.n4yDisplay) {
      const computedDisplay = window.getComputedStyle(element).display;
      element.dataset.n4yDisplay =
        computedDisplay !== 'none' ? computedDisplay : element.tagName === 'HR' ? 'block' : 'flex';
    }

    if (!element.dataset.n4yMarginTop) {
      const computed = window.getComputedStyle(element);
      element.dataset.n4yMarginTop    = computed.marginTop;
      element.dataset.n4yMarginBottom = computed.marginBottom;
      element.dataset.n4yPaddingTop   = computed.paddingTop;
      element.dataset.n4yPaddingBottom = computed.paddingBottom;
    }

    const targetDisplay = element.dataset.n4yDisplay;
    const isVisible =
      window.getComputedStyle(element).display !== 'none' &&
      element.getBoundingClientRect().height > 0;

    if (shouldShow === isVisible) return;

    const transition =
      'height 0.35s cubic-bezier(0.4,0,0.2,1), opacity 0.35s cubic-bezier(0.4,0,0.2,1), ' +
      'margin 0.35s cubic-bezier(0.4,0,0.2,1), padding 0.35s cubic-bezier(0.4,0,0.2,1)';

    if (element._n4yTransitionEndHandler) {
      element.removeEventListener('transitionend', element._n4yTransitionEndHandler);
      element._n4yTransitionEndHandler = null;
    }

    const { n4yMarginTop: mt, n4yMarginBottom: mb, n4yPaddingTop: pt, n4yPaddingBottom: pb } = element.dataset;

    if (shouldShow) {
      element.style.display     = targetDisplay;
      element.style.transition  = 'none';
      element.style.overflow    = 'hidden';
      element.style.height      = 'auto';
      element.style.marginTop   = mt;  element.style.marginBottom  = mb;
      element.style.paddingTop  = pt;  element.style.paddingBottom = pb;
      const targetHeight = element.scrollHeight;
      element.style.height      = '0px'; element.style.opacity     = '0';
      element.style.marginTop   = '0px'; element.style.marginBottom  = '0px';
      element.style.paddingTop  = '0px'; element.style.paddingBottom = '0px';
      element.offsetHeight;
      element.style.transition = transition;
      requestAnimationFrame(() => {
        element.style.height      = `${targetHeight}px`; element.style.opacity = '1';
        element.style.marginTop   = mt;  element.style.marginBottom  = mb;
        element.style.paddingTop  = pt;  element.style.paddingBottom = pb;
      });
      const onShowEnd = (e) => {
        if (e.propertyName !== 'height') return;
        element.style.height = ''; element.style.opacity = '';
        element.style.marginTop = mt;  element.style.marginBottom  = mb;
        element.style.paddingTop = pt; element.style.paddingBottom = pb;
        element.style.overflow = ''; element.style.transition = '';
        element.removeEventListener('transitionend', onShowEnd);
        element._n4yTransitionEndHandler = null;
      };
      element._n4yTransitionEndHandler = onShowEnd;
      element.addEventListener('transitionend', onShowEnd);
    } else {
      element.style.display    = targetDisplay;
      element.style.transition = 'none';
      element.style.overflow   = 'hidden';
      const startHeight = element.getBoundingClientRect().height;
      element.style.height      = `${startHeight}px`; element.style.opacity = '1';
      element.style.marginTop   = mt;  element.style.marginBottom  = mb;
      element.style.paddingTop  = pt;  element.style.paddingBottom = pb;
      element.offsetHeight;
      element.style.transition = transition;
      requestAnimationFrame(() => {
        element.style.height      = '0px'; element.style.opacity = '0';
        element.style.marginTop   = '0px'; element.style.marginBottom  = '0px';
        element.style.paddingTop  = '0px'; element.style.paddingBottom = '0px';
      });
      const onHideEnd = (e) => {
        if (e.propertyName !== 'height') return;
        element.style.display = 'none';
        element.style.height = ''; element.style.opacity = '';
        element.style.marginTop = mt;  element.style.marginBottom  = mb;
        element.style.paddingTop = pt; element.style.paddingBottom = pb;
        element.style.overflow = ''; element.style.transition = '';
        element.removeEventListener('transitionend', onHideEnd);
        element._n4yTransitionEndHandler = null;
      };
      element._n4yTransitionEndHandler = onHideEnd;
      element.addEventListener('transitionend', onHideEnd);
    }
  }

  function showAllSections() {
    // These are always visible — bypass animation to avoid height-lock bug
    // when the outer container is still display:none on first calculate().
    const alwaysVisible = [
      el('how-you-pay-section'),
      el('admin-fee-summary'),
      el('total-payable-without-vat-summary'),
      el('pay-to-us-summary'),
      el('pay-to-courier-amount-summary'),
      el('admin-fee-summary-divider')
    ];
    alwaysVisible.forEach((id) => {
      const node = document.getElementById(id);
      if (!node) return;
      if (node._n4yTransitionEndHandler) {
        node.removeEventListener('transitionend', node._n4yTransitionEndHandler);
        node._n4yTransitionEndHandler = null;
      }
      node.style.display = ''; node.style.height = '';  node.style.opacity = '';
      node.style.overflow = ''; node.style.transition = '';
      node.style.marginTop = ''; node.style.marginBottom = '';
      node.style.paddingTop = ''; node.style.paddingBottom = '';
    });

    const tr = window.CartTranslations || {};
    updateElement(el('estimated-total-incl-vat'), tr.total_incl_vat || 'Total (Incl. VAT)');
  }

  // =====================================================
  // Event listeners — country and shipping rate selection
  // Event names use the country code prefix so the same file works
  // for AE (UAE-shipping-*), SA (SA-shipping-*), QA (QA-shipping-*), etc.
  // =====================================================
  window.addEventListener(`${countryEventPrefix}-shipping-country-selected`, (event) => {
    try {
      _log.log('Country selected:', event.detail.code);
      currentCountryCode = event.detail.code;
      currentShippingRateWithVAT = 0;
      calculate();
    } catch (err) {
      _log.warn('Country selection error:', err);
    }
  });

  window.addEventListener(`${countryEventPrefix}-shipping-rate-selected`, (event) => {
    try {
      const shippingRate = event.detail.shipping.price * 100;
      _log.log('Shipping rate selected:', shippingRate);
      currentShippingRateWithVAT = shippingRate;
      calculate();
    } catch (err) {
      _log.warn('Shipping rate selection error:', err);
    }
  });

  window.addEventListener('cart-api-stock-data-ready', () => {
    try {
      _log.log('API stock data ready, recalculating');
      calculate();
    } catch (err) {
      _log.warn('API stock data event error:', err);
    }
  });

  window.addEventListener('cart-forklift-processed', (event) => {
    try {
      _log.log('Cart processed by forklift, recalculating totals');
      if (event.detail?.cartData) {
        updateCartData(event.detail.cartData);
        calculate();
      } else {
        _log.warn('No cart data in forklift event');
      }
    } catch (err) {
      _log.warn('Cart forklift processed event error:', err);
    }
  });

  // =====================================================
  // Initial calculation + visibility observer
  // =====================================================
  calculate();

  const container = document.getElementById('vat-excl-totals-cart-summary');
  _log.log('Container element:', container);

  if (container) {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          if (container.style.display !== 'none') {
            _log.log('Container became visible, recalculating...');
            calculate();
          }
        }
      });
    });
    observer.observe(container, { attributes: true, attributeFilter: ['style'] });
    _log.log('Visibility observer attached');
  } else {
    _log.warn('Container #vat-excl-totals-cart-summary not found, observer not attached');
  }

  _log.log('Module loaded');
});
